import React, { useEffect, useState } from "react";
import { inject, observer } from "mobx-react";
import { NaverTop } from "../../components/top/Top";
import { NaverTitle } from "../../components/title/Title";
import {
  PageContainer,
  PageContentContainer,
  PageTitleContainer,
  BizInfoCard,
  BizContainer,
} from "../../components/common";
import { AlertModal } from "components/modal/AlertModal";
import { overlay } from "overlay-kit";
import { useHistory } from "react-router";
import Text from "components/ui/Text";
import { Button } from "components/ui/Button";
import { useNaverShopSearchQuery } from "views/naver/service/hooks/queries";
import { useNaverDisconnectMutation } from "views/naver/service/hooks/mutations";
import { fn_bridge_naver_login_exit } from "function/FnPosBridge";

interface Props {
  rootStore?: any;
}

const NaverConnectedView = inject("rootStore")(
  observer(({ rootStore }: Props) => {
    const history = useHistory();
    const { searchStore } = rootStore;
    const [currentPage, setCurrentPage] = useState(1);
    
    const shopCd = searchStore.getShopCd;
    const naverUniqueId = searchStore.getNaverUniqueId;

    useEffect(() => {
      fn_bridge_naver_login_exit(rootStore, "HIDE"); // 배달매니저 이동 버튼 삭제 호출
    },[])

    // 매장 조회
    const { data: shopSearchData } = useNaverShopSearchQuery(
      rootStore,
      {
        shopCd,
        page: currentPage,
        size: 5,
        includeNpayUnregistered: true,
      },
      { enabled: !!naverUniqueId }
    );

    // 연동 해제
    const { mutate: naverDisconnect } = useNaverDisconnectMutation(rootStore);

    const shops = shopSearchData?.data?.content || [];
    const pageInfo = shopSearchData?.data?.page;
    const totalPages = pageInfo?.totalPages || 1;
    const currentPageNumber = pageInfo?.pageNumber || 1;

    // 연동 해제
    const handleDisconnected = (shopName: string, businessId: number) => {
      overlay.open(({ isOpen, close }) => (
        <AlertModal
          open={isOpen}
          title="매장 연동을 해제할까요?"
          subTit1={`선택하신 ${shopName}과 배달매니저 연동을 해제합니다.`}
          subTit2="해제 이후에는 배달매니저에서 네이버 주문을 처리할 수 없습니다."
          leftBtnOnClick={close}
          leftBtnVariant="secondaryPrimary"
          leftBtnTxt="닫기"
          rightBtnOnClick={() => {
            close();
            naverDisconnect(
              {
                shopCd: searchStore.getShopCd,
                businessId,
              },
              {
                onSuccess: async data => {
                  overlay.open(({ isOpen }) => (
                    <AlertModal
                      open={isOpen}
                      title="연동이 해제되었어요"
                      subTit1="배달매니저 화면으로 돌아갈게요"
                      rightBtnOnClick={() => {
                        overlay.closeAll();  
                        window.location.href = "/setting/default/receipt";
                      }}
                      rightBtnVariant="secondaryPrimary"
                      rightBtnTxt="닫기"
                    />
                  ));
                },
                onError: error => {
                  overlay.open(({ isOpen, close }) => (
                    <AlertModal
                      open={isOpen}
                      title="연동 해제에 실패했어요"
                      subTit1="알 수 없는 오류가 발생했습니다."
                      subTit2="잠시 후 다시 시도해주세요."
                      rightBtnOnClick={close}
                      rightBtnVariant="secondaryPrimary"
                      rightBtnTxt="닫기"
                    />
                  ));
                },
              }
            );
          }}
          rightBtnVariant="error"
          rightBtnTxt="연동 해제하기"
        />
      ));
    };

    // 페이징 핸들러
    const handlePreviousPage = () => {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    };

    const handleNextPage = () => {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    };

    // 주문 사용 관리
    const handleOrderManagement = (businessId: number) => {
      history.push(`/naver/bizitem?businessId=${businessId}&connected=true`);
    };

    return (
      <>
        <NaverTop title="네이버 주문 연동" />
        <PageContainer>
          <PageContentContainer>
            <PageTitleContainer>
              <NaverTitle
                title="이미 연동된 매장이 있어요"
                subTitle="배달매니저와 네이버 주문 매장이 연동되면 배달매니저에서 주문을 처리할 수 있어요"
              />
            </PageTitleContainer>

            {/* 매장 리스트 */}
            <BizContainer>
              {shops.map((shop: any, index: number) => (
                <BizInfoCard
                  key={shop.businessId}
                  iconSrc={shop.image}
                  iconAlt={`${shop.shopNm} 이미지`}
                  title={shop.shopNm}
                  address={`${shop.addr} ${shop.addrDtl || ""}`.trim()}
                  phone={shop.telNo}
                  buttons={[
                    {
                      text: "주문 사용 관리",
                      variant: "secondaryLight",
                      onClick: () => handleOrderManagement(shop.businessId),
                    },
                    {
                      text: "연동 해제하기",
                      variant: "error",
                      onClick: () => handleDisconnected(shop.shopNm, shop.businessId),
                    },
                  ]}
                />
              ))}
            </BizContainer>

            {/* 페이징 - 다음 페이지가 있을 때만 표시 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-end gap-3 pt-6">
                <Text fontSize={14} fontWeight={400} color="#51525C">
                  {currentPageNumber} / {totalPages}
                </Text>
                <Button
                  fullWidth={false}
                  variant="secondaryLight"
                  size="sm"
                  padding="12px 16px"
                  disabled={currentPage === 1}
                  onClick={handlePreviousPage}
                >
                  이전
                </Button>
                <Button
                  fullWidth={false}
                  variant="secondaryLight"
                  size="sm"
                  padding="12px 16px"
                  disabled={currentPage >= totalPages}
                  onClick={handleNextPage}
                >
                  다음
                </Button>
              </div>
            )}
          </PageContentContainer>
        </PageContainer>
      </>
    );
  })
);

export default NaverConnectedView;
